import { useMemo } from "react";
import { OrganisationBillingConfig } from "@tether-web-portal/types/BillingConfig";
import { useGetOrganisationBillingConfig } from "@tether-web-portal/api/billing-config/hooks";

export interface TenantLabels {
  singular: string; // "Tenant" | "Cost Centre" | ...
  plural: string; // "Tenants" | "Cost Centres" | ...
}

/** Frozen so the shared default can't be mutated by a consumer that holds the
 * resolved labels (the null path used to hand out this exact reference). */
export const DEFAULT_TENANT_LABELS: TenantLabels = Object.freeze({
  singular: "Tenant",
  plural: "Tenants",
});

/**
 * Resolve the per-Org tenant labels. Falls back to the platform default
 * ("Tenant"/"Tenants") when config is missing or a label is blank. Always
 * returns a fresh object so callers never share/mutate the default reference.
 */
export function resolveTenantLabels(
  config: Pick<OrganisationBillingConfig, "tenantLabelSingular" | "tenantLabelPlural"> | null | undefined,
): TenantLabels {
  const singular = config?.tenantLabelSingular?.trim();
  const plural = config?.tenantLabelPlural?.trim();
  return {
    singular: singular || DEFAULT_TENANT_LABELS.singular,
    plural: plural || DEFAULT_TENANT_LABELS.plural,
  };
}

export function useTenantLabels(): TenantLabels {
  const { data } = useGetOrganisationBillingConfig();
  // Memoised so the returned object identity is stable between renders while the
  // config is unchanged (avoids effect-dependency churn in consumers).
  return useMemo(() => resolveTenantLabels(data), [data]);
}
